/**
 * Variantes de un producto de evento: alternativas del mismo producto, al
 * mismo precio y con el mismo inventario, que la caja pregunta al vender.
 * El caso típico es un cóctel que se pide con otro licor ("Squeeze" de
 * whiskey en vez del original).
 *
 * En el producto viven como `variants: string[]` (solo las alternativas: el
 * original siempre se ofrece). En la orden, cada línea lleva `variant` con
 * la alternativa elegida, o nada si es el original; así "Squeeze" y
 * "Squeeze · Whiskey" se cuentan aparte en el informe, el tiquete y la
 * comanda. Funciones puras: nada de Angular ni de Firestore.
 */
import { normalizeText } from '../product-import/product-import.model';

/** Cómo llama la caja a la opción sin variante. */
export const ORIGINAL_VARIANT_LABEL = 'Original';

/** Clave de la opción original en el desglose de la caja (nunca choca con un nombre real). */
export const ORIGINAL_VARIANT_KEY = '';

/** Separador entre el nombre del producto y su variante: "Squeeze · Whiskey". */
export const VARIANT_SEPARATOR = ' · ';

/** "whiskey" y "Whiskey " son la misma variante. */
export function variantKey(name: any): string {
    return normalizeText(name);
}

/** Nombre tal como se guarda y se muestra: sin espacios sobrantes y con la inicial en mayúscula. */
export function cleanVariantName(name: any): string {
    const clean = String(name === null || name === undefined ? '' : name).replace(/\s+/g, ' ').trim();
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : '';
}

/** Variantes de un documento de producto: limpias, sin vacías ni repetidas, en el orden en que se escribieron. */
export function parseVariants(raw: any): string[] {
    if (!Array.isArray(raw)) return [];
    const seen: { [key: string]: boolean } = {};
    const out: string[] = [];
    raw.forEach(item => {
        const name = cleanVariantName(item);
        const key = variantKey(name);
        if (!name || seen[key]) return;
        seen[key] = true;
        out.push(name);
    });
    return out;
}

export function hasVariants(brick: any): boolean {
    return parseVariants(brick && brick.variants).length > 0;
}

/** Opciones que ofrece la caja: el original primero y luego cada variante. */
export function variantOptions(brick: any): string[] {
    return [ORIGINAL_VARIANT_KEY].concat(parseVariants(brick && brick.variants));
}

export function variantLabel(variant: string): string {
    return variant || ORIGINAL_VARIANT_LABEL;
}

/** Variante de una línea de orden, limpia; '' si es el original o la orden es anterior a las variantes. */
export function lineVariant(line: any): string {
    return cleanVariantName(line && line.variant);
}

/** "Squeeze · Whiskey"; sin variante, el nombre tal cual. */
export function withVariant(name: any, variant: any): string {
    const base = String(name === null || name === undefined ? '' : name).replace(/\s+/g, ' ').trim();
    const extra = cleanVariantName(variant);
    return extra ? `${base}${VARIANT_SEPARATOR}${extra}` : base;
}

/**
 * Nombre con el que una línea de orden se muestra y se cuenta: el producto
 * más su variante. La caja copia el producto dentro de la línea
 * (`line.brick`); las órdenes viejas traen el nombre en la línea misma.
 */
export function lineName(line: any, fallback: string = 'Producto'): string {
    const name = (line && line.brick && line.brick.name) || (line && line.name) || fallback;
    return withVariant(name, lineVariant(line));
}

// ─── Desglose en la caja ───────────────────────────────────────────────────
// Mientras se arma la orden, la presentación del producto lleva
// `variant_quantities`: cuántas unidades van de cada opción (clave '' para el
// original). `quantity` sigue siendo el total, que es lo que el inventario y
// el subtotal necesitan.

export interface VariantQuantities {
    [variant: string]: number;
}

export function variantQuantities(presentation: any): VariantQuantities {
    const raw = presentation && presentation.variant_quantities;
    if (!raw || typeof raw !== 'object') return {};
    const out: VariantQuantities = {};
    Object.keys(raw).forEach(key => {
        const qty = Number(raw[key]);
        if (isFinite(qty) && qty > 0) out[key] = qty;
    });
    return out;
}

export function variantQuantity(presentation: any, variant: string): number {
    return variantQuantities(presentation)[variant || ORIGINAL_VARIANT_KEY] || 0;
}

export function variantTotal(quantities: VariantQuantities): number {
    return Object.keys(quantities).reduce((sum, key) => sum + Number(quantities[key] || 0), 0);
}

/** Nuevo desglose con la opción en `qty` (se quita si llega a 0). No toca el original. */
export function setVariantQuantity(presentation: any, variant: string, qty: number): VariantQuantities {
    const next = variantQuantities(presentation);
    const key = variant || ORIGINAL_VARIANT_KEY;
    if (qty > 0) next[key] = qty;
    else delete next[key];
    return next;
}

/** "2 Original · 1 Whiskey", solo con las opciones que llevan unidades; '' si no hay variantes elegidas. */
export function variantBreakdown(presentation: any): string {
    const quantities = variantQuantities(presentation);
    const keys = Object.keys(quantities);
    if (keys.length === 0 || (keys.length === 1 && keys[0] === ORIGINAL_VARIANT_KEY)) return '';
    return keys.map(key => `${quantities[key]} ${variantLabel(key)}`).join(VARIANT_SEPARATOR);
}

/** Una línea de orden por cada opción con unidades, a partir de la línea única del carrito. */
export interface VariantSplit {
    variant: string;
    quantity: number;
}

export function splitByVariant(presentation: any): VariantSplit[] {
    const quantities = variantQuantities(presentation);
    return Object.keys(quantities).map(key => ({ variant: key, quantity: quantities[key] }));
}
